import {
    transformContainerItem,
    transformItem,
} from '@/components/OrdersModule/OrderDetail/utils/transforms'
import Overview from '@/features/common/components/Overview'
import ProductItem from '@/features/common/components/Overview/parts/ProductItem'
import { PriceItem } from '@/features/common/components/Overview/types'
import { ContainerItem, Item, Price } from '@/features/orders/types/app'
import { useScreenLayout } from '@/hooks/screen'
import { ItemsPriceCalculator } from '@/services/calculators/itemsPriceCalculator'
import { formatFreeOfChargeAblePrice } from '@/utils/formatFreeOfChargeAblePrice'
import { FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface OrderDetailItemListProps {
    items: Item[]
    containerItems: ContainerItem[]
    deliveryPrice?: Price
    smallOrderSurcharge?: Price
    tip?: Price
    totalPrice: Price
    currency: string
}

const OrderDetailItemList: FC<OrderDetailItemListProps> = ({
    items,
    containerItems,
    deliveryPrice,
    smallOrderSurcharge,
    tip,
    totalPrice,
    currency,
}) => {
    const { t } = useTranslation()
    const layout = useScreenLayout()

    const productItems = useMemo(() => items.map((item) => transformItem(item, t)), [items, t])
    const standaloneContainerItems = useMemo(
        () => containerItems.map((containerItem) => transformContainerItem(containerItem, t)),
        [containerItems, t]
    )
    const formatPriceWithCents = (price: Price) => formatFreeOfChargeAblePrice(price, t, false, 2)

    const priceItems: PriceItem[] = [
        {
            label: t('common.overview.prices.items'),
            price: formatPriceWithCents(ItemsPriceCalculator.calculateTotalPrice(items, currency)),
        },
    ]

    if (deliveryPrice) {
        priceItems.push({
            label: t('common.overview.prices.delivery'),
            price: formatPriceWithCents(deliveryPrice),
        })
    }

    if (
        standaloneContainerItems.length > 0 ||
        productItems.some((item) => item.containers?.length)
    ) {
        priceItems.push({
            label: t('common.overview.prices.container'),
            price: formatPriceWithCents(
                ItemsPriceCalculator.calculateTotalContainerPrice(items, containerItems, currency)
            ),
        })
    }

    if (smallOrderSurcharge) {
        priceItems.push({
            label: t('common.overview.prices.smallOrderSurcharge'),
            price: formatPriceWithCents(smallOrderSurcharge),
        })
    }

    if (tip && +tip.amount !== 0) {
        priceItems.push({
            label: t('common.overview.prices.tip'),
            price: formatPriceWithCents(tip),
        })
    }

    return (
        <Overview
            itemList={[...productItems, ...standaloneContainerItems].map((item) => (
                <ProductItem key={item.id} productItem={item} compact={layout === 'compact'} />
            ))}
            priceItems={priceItems}
            totalPrice={formatPriceWithCents(totalPrice)}
        />
    )
}

export default memo(OrderDetailItemList)
